import { Archive, File, FileImage, FolderOpen } from "@phosphor-icons/react";
import { getFileInfo, getFileTypeDescription } from "../../utils/file-types";

interface FilePlaceholderProps {
  filename: string;
  className?: string;
}

export function FilePlaceholder({
  filename,
  className = "",
}: FilePlaceholderProps) {
  const fileInfo = getFileInfo(filename);
  const typeDescription = getFileTypeDescription(filename);

  const getIcon = () => {
    switch (fileInfo.type) {
      case "directory":
        return <FolderOpen size={48} className="text-r8-gray-9" />;
      case "safe_image":
        return <FileImage size={48} className="text-r8-blue-9" />;
      case "image":
        return <FileImage size={48} className="text-r8-blue-9" />;
      case "binary":
        if (filename.match(/\.(zip|tar|gz|7z|rar)$/i)) {
          return <Archive size={48} className="text-r8-yellow-9" />;
        }
        return <File size={48} className="text-r8-gray-9" />;
      default:
        return <File size={48} className="text-r8-gray-9" />;
    }
  };

  const getMessage = () => {
    switch (fileInfo.type) {
      case "directory":
        return {
          title: "Directory",
          description: "This is a directory in the model source.",
        };
      case "safe_image":
        return {
          title: "Safe image ready for preview",
          description: `This ${typeDescription.toLowerCase()} can be safely displayed.`,
        };
      case "image":
        return {
          title: "Image preview not available",
          description: `This ${typeDescription.toLowerCase()} cannot be displayed safely in the web editor.`,
        };
      case "binary":
        return {
          title: "Binary file preview not available",
          description: `This ${typeDescription.toLowerCase()} cannot be displayed in the web editor.`,
        };
      default:
        return {
          title: "File cannot be displayed",
          description: "This file type is not supported for preview.",
        };
    }
  };

  const { title, description } = getMessage();

  return (
    <div
      className={`min-h-[400px] flex items-center justify-center p-8 ${className}`}
    >
      <div className="text-center max-w-md">
        <div className="mb-4 flex justify-center">{getIcon()}</div>
        <h3 className="text-r8-lg font-semibold text-r8-gray-12 mb-2">
          {title}
        </h3>
        <p className="text-r8-sm text-r8-gray-11 mb-4">{description}</p>
        <div className="text-r8-xs text-r8-gray-10 bg-r8-gray-2 px-2 py-1 inline-block">
          {filename} • {typeDescription}
        </div>
      </div>
    </div>
  );
}
